import axios, {AxiosRequestConfig, AxiosResponse, AxiosError} from 'axios';
import { ZUUL_API_BASE } from '../utils/constants';
import { ZuulAuditEvent } from '../components/AuditModal/types.ts';
import { cloneDeep, unset, get } from 'lodash';

// Create a separate axios instance for audit logging to avoid circular logging
const auditApi = axios.create({
  baseURL: ZUUL_API_BASE,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Content-Security-Policy': 'upgrade-insecure-requests',
    mode: 'cors',
  },
});

/**
 * Attempt to extract a created entity id from a response
 */
const getIdFromResponse = (response?: AxiosResponse | undefined): string | undefined => {
  if (!response) return undefined;
  const data: any = response.data || {};
  // Common id field names across app
  const possibleKeys = [
    'id',
    'offer.id',
    'sequence.id',
  ];
  for (const k of possibleKeys) {
    const value = get(data, k);
    if (value) return String(value);
  }

  // Try Location header last path segment
  const locationHeader = response.headers?.location || response.headers?.Location;
  if (locationHeader && typeof locationHeader === 'string') {
    try {
      const url = new URL(locationHeader, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const last = parts[parts.length - 1];
      if (last) return last;
    } catch (_) {
      // Fallback: naive split
      const parts = String(locationHeader).split('/').filter(Boolean);
      const last = parts[parts.length - 1];
      if (last) return last;
    }
  }
  return undefined;
};

/**
 * Attempt to extract an entity id from the request URL (typically for PUT/DELETE)
 * - Supports numeric ids and UUID-like ids
 * - Looks for the last non-empty path segment that looks like an id
 */
const getIdFromRequest = (request?: AxiosRequestConfig | undefined): string | undefined => {
  try {
    if (!request?.url) return undefined;
    const url = String(request.url);
    // Strip query string and hash
    const [path] = url.split(/[?#]/);

    if (!path) return undefined;

    const parts = path.split('/').filter(Boolean);
    if (parts.length === 0) return undefined;
    // Walk from the end to find the first segment that looks like an id
    for (let i = parts.length - 1; i >= 0; i--) {
      const seg = parts[i];
      // Numeric id
      if (!seg) continue;

      if (/^\d+$/.test(seg)) return seg;
      // UUID (8-4-4-4-12 hex) or general hex-ish id
      if (/^[0-9a-fA-F-]{8,}$/.test(seg)) return seg;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Build the UI details route for create (POST) events so audit logs point to the
 * details screen users see after creation.
 * - If current path contains '/create', replace it with the new id.
 * - If creation happens from a summary screen (modal), map list path to detail path with id.
 * - Otherwise, default to current pathname.
 */
const getCurrentRoute = (request?: AxiosRequestConfig | undefined, response?: AxiosResponse | undefined) => {
  try {
    const currentPath = window.location.pathname || '/unknown';

    const method = (request?.method || '').toUpperCase();

    const requestUrlsToSkipIdMatching = ['offerwalloffers/with-offer']

    if (requestUrlsToSkipIdMatching.some(url => request?.url?.includes(url))) {
      return currentPath;
    }

    // Handle CREATE mapping for POSTs
    if (method === 'POST') {
      let id = getIdFromResponse(response);

      if (request?.url === 'offerwalloffers' && response?.data?.offer?.id){
        id = response.data.offer.id;
      }

      if (request?.url === 'sequences/publish' && response?.data?.sequence?.id){
        id = response.data.sequence.id;
      }

      if (!id) return currentPath;

      if (currentPath.endsWith('/create')) {
        return currentPath.replace(/\/create$/, `/${id}`);
      }

      const mappings: Array<{ match: RegExp; build: (id: string) => string }> = [
        { match: /^\/campaigns\/offertable\/?$/i, build: (x) => `/campaigns/offertable/offer/${x}` },
        { match: /^\/campaigns\/offerblocks\/?$/i, build: (x) => `/campaigns/offerblocks/${x}` },
        { match: /^\/campaigns\/dynamic-offer-sections\/?$/i, build: (x) => `/campaigns/dynamic-offer-sections/${x}` },
        { match: /^\/campaigns\/landingpages\/landingpage-detail\/?$/i, build: (x) => `/campaigns/landingpages/${x}` },
        { match: /^\/campaigns\/offeractions\/offer-action-detail\/?$/i, build: (x) => `/campaigns/offeractions/${x}` },
        { match: /^\/campaigns\/landingpagegroups\/?$/i, build: (x) => `/campaigns/landingpagegroups/${x}` },
        { match: /^\/campaigns\/offerwallads\/?$/i, build: (x) => `/campaigns/offerwallads/${x}` },
        { match: /^\/publisher-pre-pings\/?$/i, build: (x) => `/publisher-pre-pings/${x}` },
        { match: /^\/pubxml-feeds\/?$/i, build: (x) => `/pubxml-feeds/${x}` },
        { match: /^\/advertiser-pre-pings\/?$/i, build: (x) => `/advertiser-pre-pings/${x}` },
        { match: /^\/content\/containers\/?$/i, build: (x) => `/content/containers/${x}` },
        { match: /^\/content\/layouts\/?$/i, build: (x) => `/content/layouts/${x}` },
        { match: /^\/custom-properties\/property-sets\/?$/i, build: (x) => `/custom-properties/property-sets/${x}` },
        { match: /^\/custom-properties\/entity-groups\/?$/i, build: (x) => `/custom-properties/entity-groups/${x}` },
      ];

      for (const { match, build } of mappings) {
        if (match.test(currentPath)) return build(id);
      }

      const reqUrl = request?.url || '';
      const apiHints: Array<{ includes: RegExp; build: (id: string) => string }> = [
        { includes: /offer/i, build: (x) => `/campaigns/offertable/offer/${x}` },
        { includes: /offer-action/i, build: (x) => `/campaigns/offeractions/${x}` },
        { includes: /landingpagegroups?/i, build: (x) => `/campaigns/landingpagegroups/${x}` },
        { includes: /landingpages?/i, build: (x) => `/campaigns/landingpages/${x}` },
        { includes: /offerwallads?/i, build: (x) => `/campaigns/offerwallads/${x}` },
        { includes: /publisher-pre-pings?/i, build: (x) => `/publisher-pre-pings/${x}` },
        { includes: /pubxml(-|)feeds?/i, build: (x) => `/pubxml-feeds/${x}` },
        { includes: /advertiser-pre-pings?/i, build: (x) => `/advertiser-pre-pings/${x}` },
        { includes: /containers?/i, build: (x) => `/content/containers/${x}` },
        { includes: /layouts?/i, build: (x) => `/content/layouts/${x}` },
        { includes: /handlers?/i, build: (x) => `/content/handlers/${x}` },
        { includes: /user-attributes-value-string?/i, build: (x) => `/content/userAttributes/${x}` },
        { includes: /user-attribute?/i, build: (x) => `/content/userAttributes/${x}` },
        { includes: /property-sets?/i, build: (x) => `/custom-properties/property-sets/${x}` },
        { includes: /entity-groups?/i, build: (x) => `/custom-properties/entity-groups/${x}` },
      ];

      for (const { includes, build } of apiHints) {
        if (includes.test(reqUrl || '')) return build(id);
      }

      return currentPath;
    }

    // Handle UPDATE mapping for PUTs coming from summary screens (modal edits)
    if (method === 'PUT') {

      const routesToSkipIdMatching = ['offerblocks']

      if (routesToSkipIdMatching.some(route => currentPath.includes(route))) {
        return currentPath;
      }

      const id = getIdFromRequest(request);
      if (!id) return currentPath; // cannot route without id

      const reqUrl = request?.url || '';
      const putHints: Array<{ includes: RegExp; build: (id: string) => string }> = [
        // Campaigns/Offers family
        { includes: /offers?\//i, build: (x) => `/campaigns/offertable/offer/${x}` },
        { includes: /offer-actions?\//i, build: (x) => `/campaigns/offeractions/${x}` },
        { includes: /offerwallads?\//i, build: (x) => `/campaigns/offerwallads/${x}` },
        { includes: /landingpagegroups?\//i, build: (x) => `/campaigns/landingpagegroups/${x}` },
        { includes: /landingpages?\//i, build: (x) => `/campaigns/landingpages/${x}` },
        { includes: /dynamic-offer-sections?\//i, build: (x) => `/campaigns/dynamic-offer-sections/${x}` },
        // Standalone sections from screenshot/list
        { includes: /advertisers?\//i, build: (x) => `/advertisers/${x}` },
        { includes: /marketing[_-]?partners?\//i, build: (x) => `/marketingpartners/${x}` },
        { includes: /affiliates?\//i, build: (x) => `/affiliates/${x}` },
        { includes: /domains?\//i, build: (x) => `/domains/${x}` },
        { includes: /handlers?\//i, build: (x) => `/content/handlers/${x}` },
        { includes: /revshare-percentage(-rules)?\//i, build: (x) => `/revshare-percentage/${x}` },
        { includes: /properties?\//i, build: (x) => `/custom-properties/properties/${x}` },
        // Content
        { includes: /containers?\//i, build: (x) => `/content/containers/${x}` },
        { includes: /layouts?\//i, build: (x) => `/content/layouts/${x}` },
        { includes: /user-attributes-value-string?\//i, build: (x) => `/content/userAttributes/${x}` },
        { includes: /user-attributes?\//i, build: (x) => `/content/userAttributes/${x}` },
        // Misc known
        { includes: /publisher-pre-pings?\//i, build: (x) => `/publisher-pre-pings/${x}` },
        { includes: /advertiser-pre-pings?\//i, build: (x) => `/advertiser-pre-pings/${x}` },
        { includes: /pubxml(-|)feeds?\//i, build: (x) => `/pubxml-feeds/${x}` },
        { includes: /property-sets?\//i, build: (x) => `/custom-properties/property-sets/${x}` },
        { includes: /entity-groups?\//i, build: (x) => `/custom-properties/entity-groups/${x}` },
        { includes: /users?\//i, build: (x) => `/userdetails/${x}` },
      ];

      for (const { includes, build } of putHints) {
        if (includes.test(reqUrl)) return build(id);
      }

      // As a last resort, if the current path is a list we know, attach id to it
      const listToDetailOnPut: Array<{ match: RegExp; build: (id: string) => string }> = [
        { match: /^\/advertisers\/?$/i, build: (x) => `/advertisers/${x}` },
        { match: /^\/marketingpartners\/?$/i, build: (x) => `/marketingpartners/${x}` },
        { match: /^\/affiliates\/?$/i, build: (x) => `/affiliates/${x}` },
        { match: /^\/domains\/?$/i, build: (x) => `/domains/${x}` },
        { match: /^\/campaigns\/dynamic-offer-sections\/?$/i, build: (x) => `/campaigns/dynamic-offer-sections/${x}` },
        { match: /^\/content\/handlers\/?$/i, build: (x) => `/content/handlers/${x}` },
        { match: /^\/revshare-percentage\/?$/i, build: (x) => `/revshare-percentage/${x}` },
        { match: /^\/custom-properties\/properties\/?$/i, build: (x) => `/custom-properties/properties/${x}` },
        { match: /^\/campaigns\/offertable\/?$/i, build: (x) => `/campaigns/offertable/offer/${x}` },
      ];
      for (const { match, build } of listToDetailOnPut) {
        if (match.test(currentPath)) return build(id);
      }

      return currentPath;
    }

    // Default: don't rewrite for other methods
    return currentPath;
  } catch (error) {
    console.warn('Failed to get current route:', error);
    return '/unknown';
  }
};

const AUDIT_FILTER_KEYS: string[] = [
  'entity.offer.image.encodedImageData',
  'entity.offer.image1.encodedImageData',
  'entity.offer.image2.encodedImageData',
  'entity.image.encodedImageData',
  'entity.image1.encodedImageData',
  'entity.image2.encodedImageData',
  'offer.image.encodedImageData',
  'offer.image1.encodedImageData',
  'offer.image2.encodedImageData',
  'image.encodedImageData',
  'image1.encodedImageData',
  'image2.encodedImageData',
  'layout.content',
  'container.content',
  'entity.layout.content',
  'entity.container.content',
];

const deepOmit = (obj: any, paths: string[]): any => {
  if (!obj || typeof obj !== 'object') return obj;

  const result = cloneDeep(obj);
  paths.forEach(path => unset(result, path));
  return result;
};

const filterUnwantedData = (data: Record<any, any>, keysToFilter: string[] = AUDIT_FILTER_KEYS): string => {
  if (!data || keysToFilter.length === 0) {
    return "";
  }

  const filtered = deepOmit(data, keysToFilter);
  return JSON.stringify(filtered);
};


/**
 * Create audit log entry
 */
const createLogEntry = (request: AxiosRequestConfig, response: AxiosResponse, error: AxiosError | undefined): ZuulAuditEvent => {
  const timestamp = new Date().toISOString();
  const user = JSON.parse(localStorage.getItem('userData') as string);
  const route = getCurrentRoute(request, response);

  return {
    timestamp,
    date: new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York',
    }).split('/').reverse().join('-').replace(/(\d{4})-(\d{2})-(\d{2})/, '$1-$3-$2'),
    hour: new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      hour12: false,
      timeZone: 'America/New_York',
    }).split(':')[0] || "unknown",
    user: user?.realname || user?.email || 'unknown',
    route,
    request: {
      method: request.method?.toUpperCase(),
      url: request.url,
      baseURL: request.baseURL,
      data: filterUnwantedData(JSON.parse(request?.data || '{}')),
    },
    response: response ? {
      status: response.status,
      statusText: response.statusText,
    } : undefined,
    error: error?.message
  };
};

/**
 * Check if request should be audited (non-GET requests only)
 */
const shouldAuditRequest = (config: any) => {
  const method = config.method?.toLowerCase();
  const routeBlacklist: string[] = ['events', 'zuulusers', 'media-library', 'tags'];
  const isBlacklisted = routeBlacklist.some((route) => config.url?.includes(route));
  return method && method !== 'get' && !isBlacklisted;
};

export {
  getCurrentRoute,
  createLogEntry,
  shouldAuditRequest,
  auditApi
};
